import Dataset from '@/shared/data/model/Dataset';
import { random } from 'lodash';
import { Socket, io } from 'socket.io-client';
import { Store } from 'vuex';
import AppConstants from '../../model/AppConstants';
import Actions from '../../model/store/actions';
import State from '../../model/store/state';
import { CnctProTokenManager } from '../CnctProTokenManager';

/* eslint-disable no-console */

interface DatasetIdentity {
  customerId: string;
  siteId: string;
  name: string;
}

export class RealtimeWithDataService {
  private socket: Socket;
  private tokenManager: CnctProTokenManager;
  private retryTimeout = -1;

  private urls: string[];
  private i: number;
  log: (message: string, ...some: any[]) => void;

  constructor(private store: Store<State>) {
    const urlsString = process.env.VUE_APP_DATASERVICE_URLS;
    this.urls = urlsString.split(',');
    this.i = random(0, this.urls.length - 1);
    this.tokenManager = new CnctProTokenManager();
    this.socket = io({ autoConnect: false });
    this.log = (message: string, ...some) =>
      console.debug(`[Realtime]: ${message}`, ...some);
  }

  async connect() {
    this.nextUrl();

    const urlObject = new URL(this.url);
    const host = `${urlObject.protocol}//${urlObject.host}`;
    this.log('Connecting to', urlObject.origin);

    // Création d'un socket avec les paramètres de connexion
    this.socket = io(host, {
      autoConnect: false,
      reconnection: true,
      auth: (cb) => {
        this.tokenManager.getToken().then((token) => {
          cb({ token });
        });
      },
      path: urlObject.pathname !== '/' ? urlObject.pathname : undefined,
    });
    this.socket.connect();
    this.socket.on('connect', () => {
      this.onConnect();
    });
    this.socket.on('connect_error', (e) => this.onError(e));
    this.socket.on('error', (e) => this.onError(e));
  }

  register() {
    const registerTo = new Set<DatasetIdentity>();
    // Parcours des datasets utilisés pour récupérer les noms RD
    Object.keys(this.store.state.datasets).forEach((alias: string) => {
      const realName: string = this.store.getters.value(alias, '$dataset_name');
      if (realName) {
        const identity =
          RealtimeWithDataService.datasetStringNameToIdentity(realName);
        registerTo.add(identity);
      }
    });
    // Enregistrement
    if (this.socket.connected) {
      this.socket.emit('register', Array.from(registerTo), (ack: string) => {
        this.log('Registered', registerTo, 'with ack', ack);
      });
    }
  }

  onUpdate(updatedDatasetIdentity: DatasetIdentity, newData: Dataset) {
    this.log('Update', updatedDatasetIdentity, newData);

    // Pour chaque ds référencé ayant pour nom RD celui de l'update : mise à jour
    Object.keys(this.store.state.datasets).forEach((datasetAlias) => {
      const values = this.store.getters.all(datasetAlias);
      const realName = values.$dataset_name;
      if (realName) {
        const comparedIdentity =
          RealtimeWithDataService.datasetStringNameToIdentity(realName);
        if (
          RealtimeWithDataService.areSameDatasets(
            comparedIdentity,
            updatedDatasetIdentity,
          )
        ) {
          this.store.dispatch(Actions.DATASET_SET_MULTIPLE, {
            name: datasetAlias,
            values: newData,
          });
        }
      }
    });
  }

  onConnect() {
    this.log('Connected');
    this.dispatchConnectionStatusUpdate(true);
    this.register();
    this.socket.on('update', this.onUpdate.bind(this));
    this.socket.on('disconnect', (reason) => {
      this.dispatchConnectionStatusUpdate(false);
      this.log('Disconnected', reason);
    });
  }

  onError(e: any) {
    this.log('Socket error', e);
    // Retry in 10 seconds (with the next url)
    // this.retryTimeout = window.setTimeout(() => {
    //   this.connect();
    // }, 10000);
  }

  clearTimeouts() {
    window.clearTimeout(this.retryTimeout);
  }

  get url() {
    return this.urls[this.i];
  }

  private nextUrl() {
    if (this.i + 1 >= this.urls.length) this.i = 0;
    else this.i += 1;
  }

  private dispatchConnectionStatusUpdate(status: boolean) {
    this.store.dispatch(Actions.DATASET_SET, {
      name: AppConstants.APP_DATASET_NAME,
      key: 'online',
      value: status,
    });
  }

  static datasetStringNameToIdentity(
    datasetStringName: string,
  ): DatasetIdentity {
    const parts = datasetStringName.split(' ');
    if (parts.length === 3) {
      const [customerId, siteId, name] = parts;
      return { customerId, siteId, name };
    }
    throw new Error(
      `Can't convert dataset string name to identity: ${datasetStringName}`,
    );
  }

  static areSameDatasets(a: DatasetIdentity, b: DatasetIdentity) {
    return (
      a.customerId === b.customerId &&
      a.siteId === b.siteId &&
      a.name === b.name
    );
  }
}
